import React, { useState, useEffect } from 'react';
import { Container, Box, Text, Title, Button, List } from '@mantine/core';
import { Form, InputGroup } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoArrowBackSharp } from 'react-icons/io5';
import moment from 'moment';
import classNames from 'classnames';
import CenterPolicyModal from '../../components/modal/CenterPolicyModal';
// Components
import Loader from '../../components/Loader/Loader';
import PageTitle from '../../components/PageTitle/PageTitle';
import Footer from '../../components/Footer/Footer';
import SmartThumbnail from '../../components/SmartThumbnail/SmartThumbnail';

// Utility Service
import {
    getCountryCode,
    getCurrencyText,
    getFeeFromBookingAmount,
    getTaxTypeFromCountry,
    getPlatformFeeTypeWithAmount,
    getPlatformFeeAmount,
    getTotalAmount,
    getMerchantFeeFromFeeAndBookingAmount,
    getMerchantGstFromFeeAndBookingAmount,
} from '../../helpers';

// API-Service
import { bookingService } from '../../service/api.service';

// Images
import { placeHolder } from '../../assets/images';

const Booking = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const {
        sports_facility_id,
        court_nos,
        date,
        start,
        no_of_hours,
        total_expected_cost,
        sports_name,
        sports_thumb,
        sports_end_time,
        sports_start_time,
        sports_start_bookingAmPm,
        uuid,
        facilitiesTitle,
        courts,
        timeSlots,
        id,
        isLockedBookingValue,
        booking,
        complex,
        complexImage,
    } = location.state;

    const [loading, setLoading] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [notValidated, setNotValidated] = useState(false);
    const [isLockedBooking, setIsLockedBooking] = useState(isLockedBookingValue || false);
    const [username, setUserName] = useState('');
    const [countryCode, setCountryCode] = useState(getCountryCode(complex?.info?.country));
    const [emailNotValidated, setEmailNotValidated] = useState(false);
    const [usernameNotValidated, setUsernameNotValidated] = useState(false);
    const [openedCenterPolicyModal, setOpenedCenterPolicyModal] = useState(true);
    const imageArray = complex?.images;

    // Centralized validity checks — used for both the disabled state and
    // to decide whether to show each field's error message.
    const isPhoneValid = phoneNumber !== '' && phoneNumber?.length >= 8;
    const isEmailValid = email !== '' && EMAIL_REGEX.test(email);
    const isUsernameValid = username.trim() !== '';
    const isFormValid = isPhoneValid && isEmailValid && isUsernameValid;

    useEffect(() => {
        setLoading(false);
        setIsLockedBooking(true);
        if (isLockedBooking) {
            setLoading(false);
        }
        const timeoutId = setTimeout(() => 3000);
        return function cleanup() {
            clearTimeout(timeoutId);
        };
    }, [isLockedBooking]);

    const handleSubmit = async (event) => {
        try {
            event.preventDefault();
            if (phoneNumber === '' || phoneNumber?.length < 8) {
                setNotValidated(true);
            }

            const isEmailValidOnSubmit = email !== '' && EMAIL_REGEX.test(email);

            if (!isEmailValidOnSubmit) {
                setEmailNotValidated(true);
            }

            if (username.trim() === '') {
                setUsernameNotValidated(true);
            }
            const firstChar = phoneNumber.substring(0, 1);
            let phone = phoneNumber;
            if (firstChar === '0') {
                phone = phoneNumber.substring(1);
            }

            if (isLockedBooking && phone !== '' && phone?.length >= 8 && email !== '' && isEmailValidOnSubmit && username.trim() !== '') {
                const result = await bookingService({
                    sports_facility_id,
                    court_nos,
                    date,
                    start,
                    no_of_hours,
                    is_deposit: booking?.info?.deposit_amount > 0,
                    user_email: email,
                    user_phone: countryCode + phone,
                    user_name: username,
                    uuid,
                });
                if (result?.data?.success === 1) {
                    setLoading(false);
                    /* Payment methods
                    "Cash/Terminal/Ipay/"
                    */
                    navigate('/payment', {
                        state: {
                            desc: `Kiosk-${result.data.data.id}-${sports_name}-${date}-${start}`,
                            refNo: result?.data?.data?.id,
                            price: result?.data?.data?.final_amount,
                            paymentUrl: result?.data?.data?.payment_url,
                            remark: 'AFA-KIOSK',
                            paymentMethod: 'Ipay',
                            sports_facility_id,
                            court_nos,
                            date,
                            start,
                            no_of_hours,
                            total_expected_cost,
                            sports_name,
                            sports_thumb,
                            sports_end_time,
                            sports_start_time,
                            sports_start_bookingAmPm,
                            phoneNumber: countryCode + phone,
                            uuid,
                            complexImage,
                            facilitiesTitle,
                            courts,
                            timeSlots,
                            id,
                            isLockedBookingValue: isLockedBooking,
                        },
                    });
                } else {
                    setLoading(false);
                    navigate('/error', {
                        state: {
                            sports_error_msg: result?.data?.message,
                            sports_facility_id,
                            court_nos,
                            date,
                            start,
                            no_of_hours,
                            total_expected_cost,
                            sports_name,
                            sports_thumb,
                            sports_end_time,
                            sports_start_time,
                            sports_start_bookingAmPm,
                            phoneNumber: countryCode + phone,
                            uuid,
                            facilitiesTitle,
                            courts,
                            timeSlots,
                            id,
                            isLockedBookingValue: isLockedBooking,
                        },
                    });
                }
            }
        } catch (error) {
            const firstChar = phoneNumber.substring(0, 1);
            let phone = phoneNumber;
            if (firstChar === '0') {
                phone = phoneNumber.substring(1);
            }
            setLoading(false);
            navigate('/error', {
                state: {
                    sports_error_msg: error || error?.response?.data?.message,
                    sports_facility_id,
                    court_nos,
                    date,
                    start,
                    no_of_hours,
                    total_expected_cost,
                    sports_name,
                    sports_thumb,
                    sports_end_time,
                    sports_start_time,
                    sports_start_bookingAmPm,
                    phoneNumber: countryCode + phone,
                    uuid,
                    facilitiesTitle,
                    courts,
                    timeSlots,
                    id,
                    isLockedBookingValue: isLockedBooking,
                },
            });
        }
    };

    const handlePhoneNumber = (event) => {
        const value = event.target.value.replace(/[^0-9]/g, '');
        setNotValidated(false);
        setPhoneNumber(value);
    };

    const handlePhoneBlur = () => {
        setNotValidated(phoneNumber === '' || phoneNumber.length < 8);
    };

    const handleUsernameChange = (event) => {
        setNotValidated(false);
        setUserName(event.target.value);
    };

    const handleUsernameBlur = () => {
        setUsernameNotValidated(username.trim() === '');
    };

    const handleEmail = (event) => {
        const value = event.target.value;
        setEmail(value);
        setEmailNotValidated(false);
    };

    const handleEmailBlur = () => {
        setEmailNotValidated(email !== '' && !EMAIL_REGEX.test(email));
    };

    return (
        <Container
            fluid
            className="px-0"
            size="md"
            style={{
                background: '#F7F7F7',
            }}
        >
            <PageTitle uuid={uuid} title="" logo={imageArray && imageArray.length ? imageArray[0].url : complexImage} />
            <Container className="mb-5" size="md" px="md">
                <Container size="md" px="md">
                    <Container
                        className="p-5"
                        sx={() => ({
                            background: 'white',
                        })}
                        size="md"
                        px="md"
                    >
                        {!uuid && <p className="m-auto text-center fs-40 fw-500 booking-title">Empty/Invalid UUID</p>}
                        {uuid && loading ? (
                            <Loader />
                        ) : (
                            <Box>
                                <Box className="d-flex align-items-center mb-3">
                                    <IoArrowBackSharp
                                        role="button"
                                        size={30}
                                        className="text-dark"
                                        onClick={() => navigate(-1, { state: { uuid } })}
                                    />
                                    <p className="fs-30 fw-bold booking-title mb-0 ms-3">Booking Details</p>
                                </Box>
                                <SmartThumbnail
                                    className="booking-image"
                                    src={sports_thumb[0]}
                                    fallbackSrc={placeHolder}
                                    alt="booking image"
                                />
                                <p className="fs-18 fw-600 mt-3 booking-title">{sports_name}</p>
                                <Box>
                                    <Box className="d-flex justify-content-between my-2">
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#888686',
                                            })}
                                        >
                                            Date
                                        </Text>
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#1C1C1C',
                                            })}
                                        >
                                            {moment(date).format('Do MMM YYYY')}
                                        </Text>
                                    </Box>
                                    <Box className="d-flex justify-content-between my-2">
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#888686',
                                            })}
                                        >
                                            Start Time
                                        </Text>
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#1C1C1C',
                                            })}
                                        >
                                            {sports_start_time}
                                            {sports_start_time.includes('AM') || sports_start_time.includes('PM')
                                                ? ' '
                                                : ` ${sports_start_bookingAmPm}`}
                                        </Text>
                                    </Box>
                                    <Box className="d-flex justify-content-between my-2">
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#888686',
                                            })}
                                        >
                                            End Time
                                        </Text>
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#1C1C1C',
                                            })}
                                        >
                                            {sports_end_time}
                                        </Text>
                                    </Box>
                                    <Box className="d-flex justify-content-between my-2">
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#888686',
                                            })}
                                        >
                                            Courts
                                        </Text>
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#1C1C1C',
                                            })}
                                        >
                                            {court_nos.join(', ')}
                                        </Text>
                                    </Box>
                                    <hr />
                                    <Box className="d-flex justify-content-between my-2">
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#888686',
                                            })}
                                        >
                                            Booking Cost
                                        </Text>
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#1C1C1C',
                                            })}
                                        >
                                            {`${getCurrencyText(complex?.info?.country)}${parseFloat(
                                                booking?.final_amount
                                            )?.toFixed(2)}`}
                                        </Text>
                                    </Box>
                                    {booking?.info?.deposit_amount > 0 && (
                                        <Box className="d-flex justify-content-between my-2">
                                            <Text
                                                sx={() => ({
                                                    fontSize: 14,
                                                    color: '#888686',
                                                })}
                                            >
                                                Deposit
                                            </Text>
                                            <Text
                                                sx={() => ({
                                                    fontSize: 14,
                                                    color: '#1C1C1C',
                                                })}
                                            >
                                                {`${getCurrencyText(complex?.info?.country)}${parseFloat(
                                                    Number(booking?.info?.deposit_amount)
                                                )?.toFixed(2)}`}
                                            </Text>
                                        </Box>
                                    )}
                                    <Box className="d-flex justify-content-between mt-2">
                                        <Text
                                            sx={() => ({
                                                fontSize: 14,
                                                color: '#888686',
                                            })}
                                        >
                                            {`Platform Fee${getPlatformFeeTypeWithAmount(
                                                booking?.info,
                                                booking?.booking_amount
                                            )}`}
                                        </Text>
                                        <p className="fs-14 mb-0">{`${getCurrencyText(
                                            complex?.info?.country
                                        )}${getPlatformFeeAmount(booking?.info, booking?.booking_amount)}`}</p>
                                    </Box>
                                    <Box className="d-flex justify-content-between mt-2">
                                        <Text
                                            className="fs-14"
                                            sx={() => ({
                                                color: '#888686',
                                            })}
                                        >
                                            {`Merchant Fee(${getFeeFromBookingAmount(
                                                booking?.info?.merchant_fees,
                                                booking?.booking_amount
                                            )}%)`}
                                        </Text>
                                        <p className="fs-14 mb-0">{`${getCurrencyText(
                                            complex?.info?.country
                                        )}${getMerchantFeeFromFeeAndBookingAmount(
                                            booking?.info,
                                            booking?.booking_amount
                                        )}`}</p>
                                    </Box>
                                    <Box className="d-flex justify-content-between mt-2">
                                        <Text
                                            className="fs-14"
                                            sx={() => ({
                                                color: '#888686',
                                            })}
                                        >
                                            {`${getTaxTypeFromCountry(
                                                complex?.info?.country
                                            )}(${getFeeFromBookingAmount(
                                                booking?.info?.merchant_gst,
                                                booking?.booking_amount
                                            )}%)`}
                                        </Text>
                                        <p className="fs-14">{`${getCurrencyText(
                                            complex?.info?.country
                                        )}${getMerchantGstFromFeeAndBookingAmount(
                                            booking?.info,
                                            booking?.booking_amount
                                        )}`}</p>
                                    </Box>
                                    <hr className="mt-0 mb-1" />
                                    <Box
                                        className={classNames('d-flex justify-content-between', {
                                            'mt-2': booking?.info?.deposit_amount > 0,
                                            'my-2': !booking?.info?.deposit_amount,
                                        })}
                                    >
                                        <p className="fs-16 fw-600 mb-0">Total Amount</p>
                                        <p className="fs-16 fw-600 mb-0">
                                            {getTotalAmount(
                                                booking?.info,
                                                booking?.booking_amount,
                                                complex?.info?.country
                                            )}
                                        </p>
                                    </Box>
                                    {booking?.info?.deposit_amount > 0 && (
                                        <>
                                            <Box
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    margin: '4px 0',
                                                }}
                                            >
                                                <Text
                                                    sx={() => ({
                                                        fontSize: 16,
                                                        fontWeight: 600,
                                                        color: '#1C1C1C',
                                                    })}
                                                >
                                                    Remaining Amount
                                                </Text>
                                                <Text
                                                    sx={() => ({
                                                        fontSize: 16,
                                                        fontWeight: 600,
                                                        color: '#FF0000',
                                                    })}
                                                >
                                                    {`${getCurrencyText(complex?.info?.country)}${parseFloat(
                                                        Number(booking?.final_amount) -
                                                            Number(booking?.info?.deposit_amount)
                                                    )?.toFixed(2)}`}
                                                </Text>
                                            </Box>
                                            <Box
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    margin: '4px 0px 8px 0px',
                                                }}
                                            >
                                                <Text
                                                    sx={() => ({
                                                        fontSize: 14,
                                                        fontWeight: 400,
                                                        color: '#FA5000',
                                                    })}
                                                >
                                                    Remaining amount need to pay onsite
                                                </Text>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                                <List>
                                    <List.Item>Please double check your booking details. </List.Item>
                                    <List.Item>No refund or cancellation allowed after booking is made.</List.Item>
                                </List>
                                <Box
                                    sx={() => ({
                                        marginTop: 20,
                                    })}
                                >
                                    <p className="fs-18 fw-600 booking-title mb-4">User Details</p>
                                    <form onSubmit={(event) => handleSubmit(event)}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 fw-600">User Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={username}
                                                onChange={(e) => handleUsernameChange(e)}
                                                onBlur={handleUsernameBlur}
                                                placeholder="Enter name"
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label className="fs-14 fw-600">Phone Number</Form.Label>
                                            <InputGroup className="rounded mb-4">
                                                <InputGroup.Text className="p-0 border-0">
                                                    <Form.Select
                                                        className="border-end-0"
                                                        onChange={(e) => {
                                                            setCountryCode(e?.target?.value);
                                                        }}
                                                    >
                                                        <option value="+60">+60</option>
                                                        <option value="+61">+61</option>
                                                        <option value="+61">+62</option>
                                                        <option value="+65">+65</option>
                                                        <option value="+855">+855</option>
                                                    </Form.Select>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    className="border-start-0"
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={(e) => handlePhoneNumber(e)}
                                                    onBlur={handlePhoneBlur}
                                                    placeholder="Phone number"
                                                    maxLength={15}
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 fw-600">Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={email}
                                                onChange={(e) => handleEmail(e)}
                                                onBlur={handleEmailBlur}
                                                placeholder="Email address"
                                            />
                                        </Form.Group>
                                        {emailNotValidated && (
                                            <Text
                                                style={{
                                                    fontSize: '16px',
                                                    fontWeight: '300',
                                                    fontStyle: 'italic',
                                                    lineHeight: 1.56,
                                                    color: '#d43d40',
                                                    marginTop: 20,
                                                }}
                                            >
                                                *Please enter a valid email to receive your e-receipt.
                                            </Text>
                                        )}
                                        {notValidated && (
                                            <Text
                                                style={{
                                                    fontSize: '16px',
                                                    fontWeight: '300',
                                                    fontStyle: 'italic',
                                                    lineHeight: 1.56,
                                                    color: '#d43d40',
                                                    marginTop: 20,
                                                }}
                                            >
                                                *Please enter a valid Phone Number
                                            </Text>
                                        )}
                                        {usernameNotValidated && (
                                            <Text
                                                style={{
                                                    fontSize: '20px',
                                                    fontWeight: '300',
                                                    fontStyle: 'italic',
                                                    lineHeight: 1.56,
                                                    color: '#d43d40',
                                                    marginTop: 20,
                                                }}
                                            >
                                                *Please enter your name
                                            </Text>
                                        )}

                                        {booking?.info?.deposit_amount > 0 ? (
                                            <>
                                                {isLockedBooking && (
                                                    <Box
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            margin: '32px 0',
                                                        }}
                                                    >
                                                        <Button
                                                            type="submit"
                                                            style={{
                                                                backgroundColor: '#FA5000',
                                                                '&:hover': {
                                                                    backgroundColor: '#FA5000',
                                                                },
                                                            }}
                                                            disabled={!isLockedBooking || !isFormValid}
                                                        >
                                                            Pay Deposit Amount
                                                        </Button>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {isLockedBooking && (
                                                    <Box className="text-end">
                                                        <Button
                                                            type="submit"
                                                            style={{
                                                                backgroundColor: '#FA5000',
                                                                '&:hover': {
                                                                    backgroundColor: '#FA5000',
                                                                },
                                                            }}
                                                            disabled={!isLockedBooking || !isFormValid}
                                                        >
                                                            Pay Now
                                                        </Button>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </form>
                                </Box>
                            </Box>
                        )}
                    </Container>
                </Container>
                <CenterPolicyModal
                    centerPolicy={openedCenterPolicyModal}
                    openedCenterPolicyModal={setOpenedCenterPolicyModal}
                    policy={complex?.info?.center_policy}
                />
            </Container>
            <Footer />
        </Container>
    );
};

export default Booking;